import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {AdyenPaymentMethodsService} from '../../../services/adyen/payment-methods'
import {AdyenEnvironmentService} from '@adyenPWA/lib/services/environment'
import {AdyenShippingMethodsService} from '../lib/services/shipping-methods'
import useShipToCountry from './../hooks/use-ship-to-country'
import {getDefaultShipment} from '@app/utils/basket-utils'
import {AdyenShippingAddressService} from '@adyenPWA/lib/services/shipping-address'

export const AdyenExpressCheckoutContext = React.createContext({})

/**
 * Fetches available payment methods for a given customer and basket using the Adyen payment service.
 *
 * This asynchronous function interacts with the AdyenPaymentMethodsService to retrieve payment methods
 * based on the provided customer and basket details. It handles potential errors by returning null
 * if the fetch operation fails.
 *
 * @async
 * @function fetchPaymentMethods
 * @param {string} customerId - The unique identifier of the customer for whom payment methods are being fetched.
 * @param {Object} site - The site object containing information about the current site context.
 * @param {string} locale - The locale string representing the language and region preferences.
 * @param {string} authToken - The authentication token required to access the Adyen service.
 * @param {string} basketID - The unique identifier of the basket for which payment methods are being fetched.
 * @param {string} shipToCountry - The default country code to ship to, used if no shipping address is present.
 * @param {Object} currentBasket - The current basket object containing shipment and shipping address details.
 * @returns {Promise<Object|null>} A promise that resolves to the available payment methods object or null if an error occurs.
 */
export const fetchPaymentMethods = async (
    customerId,
    site,
    locale,
    authToken,
    basketID,
    shipToCountry,
    currentBasket
) => {
    const adyenPaymentMethodsService = new AdyenPaymentMethodsService(authToken, site)
    try {
        let countryCode = shipToCountry

        return await adyenPaymentMethodsService.fetchPaymentMethods(
            customerId,
            locale,
            countryCode,
            basketID,
            site.id,
            true,
            currentBasket,
            shipToCountry
        )
    } catch (error) {
        return null
    }
}

export const fetchEnvironment = async (site, authToken) => {
    const adyenEnvironmentService = new AdyenEnvironmentService(authToken, site)
    try {
        return await adyenEnvironmentService.fetchEnvironment()
    } catch (error) {
        return null
    }
}

export const fetchShippingMethods = async (basketId, site, authToken, customerid) => {
    const adyenShippingMethodsService = new AdyenShippingMethodsService(authToken, site)
    try {
        return await adyenShippingMethodsService.getShippingMethods(basketId, customerid)
    } catch (error) {
        return null
    }
}

export const setDefaultShippingAddress = async (basketID, shipToCountry, site, authToken) => {
    const adyenShippingAddressService = new AdyenShippingAddressService(authToken, site)
    try {
        return await adyenShippingAddressService.updateShippingAddress(basketID, {
            deliveryAddress: {
                country: shipToCountry?.id
            },
            profile: {}
        })
    } catch (error) {
        return null
    }
}

export const AdyenExpressCheckoutProvider = ({
    children,
    authToken,
    customerId,
    locale,
    site,
    basket,
    navigate
}) => {
    const [adyenPaymentMethods, setAdyenPaymentMethods] = useState()
    const [adyenEnvironment, setAdyenEnvironment] = useState()
    const [shippingMethods, setShippingMethods] = useState()
    const {shipToCountry} = useShipToCountry()

    useEffect(() => {
        const fetchAdyenData = async () => {
            const [environment, paymentMethods] = await Promise.all([
                fetchEnvironment(site, authToken),
                fetchPaymentMethods(
                    customerId,
                    site,
                    locale,
                    authToken,
                    basket?.basketId,
                    shipToCountry.id,
                    basket
                )
            ])
            setAdyenEnvironment(environment || {error: true})
            setAdyenPaymentMethods(paymentMethods || {error: true})
        }
        if (authToken) {
            fetchAdyenData()
        }
    }, [authToken])

    useEffect(() => {
        const fetchShippingMethodsData = async () => {
            const shippingMethods = await fetchShippingMethods(
                basket?.basketId,
                site,
                authToken,
                basket?.customerInfo?.customerId
            )
            setShippingMethods(shippingMethods || {error: true})
        }

        const setDefaultShippingAddressToCart = async () => {
            await setDefaultShippingAddress(basket?.basketId, shipToCountry, site, authToken)
        }

        const defaultShipment = getDefaultShipment(basket)
        if (!defaultShipment?.shippingAddress?.countryCode) {
            setDefaultShippingAddressToCart()
        }
        if (authToken && basket && !shippingMethods) {
            fetchShippingMethodsData()
        }
    }, [basket, authToken])

    const value = {
        adyenEnvironment,
        adyenPaymentMethods,
        basket,
        locale,
        site,
        authToken,
        navigate,
        shippingMethods,
        fetchShippingMethods
    }

    return (
        <AdyenExpressCheckoutContext.Provider value={value}>
            {children}
        </AdyenExpressCheckoutContext.Provider>
    )
}

AdyenExpressCheckoutProvider.propTypes = {
    children: PropTypes.any,
    authToken: PropTypes.any,
    customerId: PropTypes.any,
    locale: PropTypes.any,
    site: PropTypes.any,
    basket: PropTypes.any,
    navigate: PropTypes.any
}

export default AdyenExpressCheckoutProvider
